import {useLastSelectedAgent} from '@github-ui/agent-sessions/hooks/use-last-selected-agent'
import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {sendEvent} from '@github-ui/hydro-analytics'
import {useLocation} from '@github-ui/react-router'
import {useNavigate, useSearchParams} from '@github-ui/use-navigate'
import {useSafeTimeout} from '@primer/react'
import throttle from 'lodash-es/throttle'
import type {FormEvent, RefObject} from 'react'
import type React from 'react'
import {useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'

import {extractChatCommandType} from '../utils/chat-commands-helpers'
import {referenceURL} from '../utils/copilot-chat-helpers'
import {
  type AgentDropdownRepository,
  type CopilotChatCommandType,
  type CopilotChatMode,
  type CopilotChatReference,
  type CopilotChatRepo,
  ModeSupportName,
  type TaskRequestReference,
} from '../utils/copilot-chat-types'
import {copilotFeatureFlags} from '../utils/copilot-feature-flags'
import {copilotLocalStorage, type SavedMessageSource} from '../utils/copilot-local-storage'
import {executePotentialSlashCommand} from '../utils/copilot-slash-commands'
// eslint-disable-next-line no-restricted-imports
import {useChatState} from '../utils/CopilotChatContext'
import {useChatManager} from '../utils/CopilotChatManagerContext'
import {useDefaultModel} from '../utils/models'
import {ChatInputValidationError} from '../utils/validation-error'
import type {AvailableCommand} from './use-available-commands'
import {useLastUsedRepository} from './use-last-used-repository'

const MAX_HEIGHT = 300
const MIN_HEIGHT = 56

interface UseChatInputProps {
  textAreaRef?: RefObject<HTMLTextAreaElement | null>
  onSubmit?: (text: string, additionalReferences?: CopilotChatReference[]) => Promise<void>
  isLoading?: boolean
  clearPastedFile?: () => void
  onChatCommandChange?: (command: CopilotChatCommandType | null) => void
  isInPortal?: boolean
  shouldClearTextOnCommandSubmit?: boolean
  shouldClearTextOnSubmit?: boolean
  canSubmitWithEmptyText?: boolean
  availableCommands?: AvailableCommand[]
  activeModeName?: string
  storageKeySuffix?: string
  initialText?: string
  /** Maximum height for the textarea input in pixels. Overrides the default MAX_HEIGHT. */
  maxTextareaHeight?: number
  /** Source of the saved message, used to differentiate messages from different experiences */
  source?: SavedMessageSource
  getAdditionalSubmitText?: () => string
  clearAdditionalSubmitContent?: () => void
}

interface UseChatInputReturn {
  /** true if the user has provided input since the last message was sent */
  typingHasStarted: boolean
  /** reference to the input textarea */
  textAreaRef: RefObject<HTMLTextAreaElement | null>
  /** reference to the textarea preview div, which is used to display rich text in the input */
  textareaPreviewRef: RefObject<HTMLDivElement | null>
  /** container whose height can be adjusted to match the user's input */
  textAreaPreviewContainerRef: RefObject<HTMLDivElement | null>
  /** the current user input */
  text: string
  /** sets the contents of the input */
  setText: React.Dispatch<React.SetStateAction<string>>
  /** loading state during agent task submission before navigation */
  isAgentTaskSubmitInProgress: boolean
  /** the current chat command extracted from the input, or null if there isn't one */
  command: CopilotChatCommandType | null
  /** sets the current chat command */
  setCommand: React.Dispatch<React.SetStateAction<CopilotChatCommandType | null>>
  /** function to call to send a message */
  handleSubmit: (e?: FormEvent) => Promise<void>
  /** function to call to send a message to a new thread */
  handleSubmitToNewThread: () => Promise<void>
  /** handler for the onScroll event of the input textarea */
  handleScroll: () => void
  /** handler for the onChange event of the input textarea */
  handleChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void
  /** call to stop response streaming */
  handleStop: () => Promise<void>
  /** handler to move cursor to the end of prompt loaded from query params */
  handleFocus: (e: React.FocusEvent<HTMLTextAreaElement>) => void
  /** most recently used repo as a reference or for agent tasks */
  lastUsedRepo: CopilotChatRepo | null
  setLastUsedRepo: (repo: CopilotChatRepo | null) => void
  /**
   * agent tasks
   */
  selectedRepo: AgentDropdownRepository | undefined
  setSelectedRepo: (repo: AgentDropdownRepository | undefined) => void
  selectedBranch: string | undefined
  setSelectedBranch: React.Dispatch<React.SetStateAction<string | undefined>>
  selectedAgent: SelectedAgent
  setSelectedAgent: (agent: SelectedAgent) => void
  initialRepoNWO?: string
  initialText?: string
}

/**
 * Helper function to determine if the current mode is an agent command.
 * Checks either the active mode name (when askModeDropdown is enabled) or the command type.
 */
export function isAgentCommand(activeModeName: string | undefined, command: CopilotChatCommandType | null): boolean {
  return copilotFeatureFlags.askModeDropdown ? activeModeName === ModeSupportName.Agent : command === 'agent'
}

/**
 * Does a bunch of stuff, too much really, to manage a chat input for a copilot chat.
 */
export function useChatInput(props: UseChatInputProps): UseChatInputReturn {
  const state = useChatState()
  const manager = useChatManager()
  const navigate = useNavigate()
  const {selectedThreadID: contextThreadID, mode, autoSubmit, model} = state
  const {
    activeModeName,
    clearPastedFile,
    isLoading,
    onSubmit,
    onChatCommandChange,
    isInPortal,
    shouldClearTextOnCommandSubmit = true,
    shouldClearTextOnSubmit = true,
    canSubmitWithEmptyText = false,
    availableCommands = [],
    storageKeySuffix,
    source = 'immersive',
    getAdditionalSubmitText,
    clearAdditionalSubmitContent,
  } = props
  // hijack the selectedThreadID if storageKeySuffix is provided. This works for agents since agents
  // entrypoints are not using the thread methods in this file. The cleaner solution would be a bigger
  // refactor.
  const selectedThreadID = storageKeySuffix ?? contextThreadID
  const savedUserMessage = copilotLocalStorage.getSavedMessage(selectedThreadID, source)
  const savedUserMessageOnError = copilotLocalStorage.getSavedUserMessageOnError(selectedThreadID)
  const {pathname} = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const [text, setText] = useState(() => {
    if (props.initialText) {
      return props.initialText
    }

    // we figure out the initial text here so it will be available in the first render
    const prompt = getPromptFromSearchParams(state.mode, pathname, searchParams)
    return prompt ?? savedUserMessageOnError ?? savedUserMessage ?? ''
  })
  const [isAgentTaskSubmitInProgress, setIsAgentTaskSubmitInProgress] = useState(false)
  const [command, setCommand] = useState<CopilotChatCommandType | null>(null)

  const [typingHasStarted, setTypingHasStarted] = useState(false)
  const internalRef = useRef<HTMLTextAreaElement>(null)
  const textAreaRef = props.textAreaRef || internalRef
  const textareaPreviewRef = useRef<HTMLDivElement>(null)
  const textAreaPreviewContainerRef = useRef<HTMLDivElement>(null)
  const recallIndex = useRef(0)

  const reloadThreadTimerRef = useRef<number | null>(null)
  // Store manager callback, which will be called when state changes
  const reloadThreadFetchMessagesCallbackRef = useRef(() => {})
  const handleAutoSubmitRef = useRef(false)
  const defaultModel = useDefaultModel(state.availableModels)
  const prevThreadID = useRef<string | null>(selectedThreadID)
  const {safeSetTimeout} = useSafeTimeout()
  const prevContainerHeight = useRef(0)

  // creating a task
  const [lastUsedRepo, setLastUsedRepo] = useLastUsedRepository()

  const [selectedRepo, setSelectedRepo] = useState<AgentDropdownRepository | undefined>(undefined)

  const repoReference = state.currentReferences.find(ref => ref.type === 'repository')
  const initialRepoNWO = repoReference
    ? `${repoReference.ownerLogin}/${repoReference.name}`
    : `${lastUsedRepo?.ownerLogin}/${lastUsedRepo?.name}`

  const onSelectRepo = useCallback((repo: AgentDropdownRepository | undefined) => {
    setSelectedRepo(repo)
  }, [])
  const [selectedBranch, setSelectedBranch] = useState<string | undefined>(undefined)

  const [selectedAgent, setSelectedAgent] = useLastSelectedAgent()

  useEffect(() => {
    if (selectedThreadID === prevThreadID.current) {
      copilotLocalStorage.setSavedMessage(selectedThreadID, source, text)
    } else {
      setText(copilotLocalStorage.getSavedMessage(selectedThreadID, source) || '')
      prevThreadID.current = selectedThreadID
    }
  }, [selectedThreadID, source, text])

  useLayoutEffect(() => {
    let tries = 0

    function adjustHeight() {
      if (!textAreaRef.current || !textAreaPreviewContainerRef.current || !textareaPreviewRef.current) return

      tries++
      if ((isInPortal || mode === 'assistive') && textAreaRef.current.scrollHeight === 0 && tries < 10) {
        // because this is (often) in a Portal, useLayoutEffect() can run before we're inserted in the DOM, and we are
        // thus unable to get a correct scrollHeight. in that case, let's try again in a bit
        safeSetTimeout(adjustHeight, 1)
        return
      }

      if (text.length === 0 && prevContainerHeight.current === MIN_HEIGHT) {
        return
      }

      textAreaRef.current.style.height = '0'
      const scrollHeight = Math.max(textAreaRef.current.scrollHeight, MIN_HEIGHT)
      const maxHeight = props.maxTextareaHeight ?? MAX_HEIGHT
      const containerHeight = Math.min(scrollHeight, maxHeight)

      textareaPreviewRef.current.style.height = `${scrollHeight}px`
      textAreaRef.current.style.height = `${scrollHeight}px`
      textAreaPreviewContainerRef.current.style.height = `${containerHeight}px`
      // Override CSS max-height when a custom maxTextareaHeight is provided, or reset it when not
      textAreaPreviewContainerRef.current.style.maxHeight = props.maxTextareaHeight
        ? `${props.maxTextareaHeight}px`
        : ''

      prevContainerHeight.current = containerHeight
    }

    adjustHeight()
  }, [mode, safeSetTimeout, text, textAreaRef, textareaPreviewRef, isInPortal, activeModeName, props.maxTextareaHeight])

  const handleFocus = (e: React.FocusEvent<HTMLTextAreaElement>) => {
    // keep the current text focus
    if (e.currentTarget.selectionStart > 0) {
      return
    }
    // else set focus to end of text
    e.currentTarget.setSelectionRange(e.currentTarget.value.length, e.currentTarget.value.length)
  }

  const handleScroll = useCallback(() => {
    if (!textAreaRef?.current || !textareaPreviewRef?.current || !textAreaPreviewContainerRef?.current) return

    if (copilotFeatureFlags.chatInputPerformanceOptimizations) {
      // Use requestAnimationFrame for smooth updates
      requestAnimationFrame(() => {
        if (!textAreaRef?.current || !textareaPreviewRef?.current) return
        textareaPreviewRef.current.scrollTop = textAreaRef.current.scrollTop
        textareaPreviewRef.current.scrollLeft = textAreaRef.current.scrollLeft
      })
    } else {
      textareaPreviewRef.current.scrollTop = textAreaRef.current.scrollTop
      textareaPreviewRef.current.scrollLeft = textAreaRef.current.scrollLeft
    }
  }, [textAreaRef])

  // Throttle to run at most once per frame (~60fps) when performance optimizations are enabled
  const throttledHandleScroll = useMemo(
    () =>
      copilotFeatureFlags.chatInputPerformanceOptimizations
        ? // eslint-disable-next-line react-hooks/refs
          throttle(handleScroll, 16, {leading: true, trailing: true})
        : handleScroll,
    [handleScroll],
  )

  // Cleanup throttled function on unmount
  useEffect(() => {
    const scrollHandler = throttledHandleScroll as ReturnType<typeof throttle> | (() => void)
    return () => {
      if (copilotFeatureFlags.chatInputPerformanceOptimizations && 'cancel' in scrollHandler) {
        scrollHandler.cancel()
      }
    }
  }, [throttledHandleScroll])

  const sendableReferences = useMemo(
    () => state.currentReferences.filter(ref => !('isClientOnly' in ref) || !ref.isClientOnly),
    [state.currentReferences],
  )

  const handleSubmit = useCallback(
    async (e?: FormEvent) => {
      setTypingHasStarted(false)

      // Clear all previously saved messages
      copilotLocalStorage.clearSavedUserMessage(selectedThreadID, source)
      copilotLocalStorage.clearSavedUserMessageOnError(selectedThreadID)

      if (state.defaultRecipient && !text.includes(`@${state.defaultRecipient}`)) {
        manager.clearDefaultRecipient()
      }

      e?.preventDefault() // TODO: fix

      const isAgent = isAgentCommand(activeModeName, command)
      // Save the selected repository to localStorage when agent task is created
      if (isAgent && selectedRepo?.id != null) {
        const result = await manager.service.fetchRepo(selectedRepo.id)
        if (result.ok && result.payload) {
          setLastUsedRepo(result.payload)
        }
      }

      // Serialize file/snippet references to include in the agent task prompt
      const serializedReferenceURLs = isAgent
        ? sendableReferences
            .filter(ref => ref.type === 'file' || ref.type === 'snippet')
            .map(ref => referenceURL(ref))
            .filter(url => url !== '#' && url !== '')
        : []

      // In agent mode, prepend additional content (e.g. code blocks) to the text before passing to
      // executePotentialSlashCommand so pasted blocks are included; in non-agent mode, wrappedOnSubmit handles this.
      const additional = isAgent ? (getAdditionalSubmitText?.() ?? '') : ''
      const effectiveInput = additional ? (text.trim() ? `${additional}\n${text}` : additional) : text
      const ranSlashCommand = await executePotentialSlashCommand(
        effectiveInput,
        state,
        manager,
        navigate,
        availableCommands,
        activeModeName,
        isAgent
          ? {
              selectedRepo,
              selectedBranch,
              selectedAgent,
              references: serializedReferenceURLs.length > 0 ? serializedReferenceURLs : undefined,
            }
          : {},
      )
      if (ranSlashCommand) {
        if (text.trim() === '/new') {
          copilotLocalStorage.setSavedMessageFast(selectedThreadID, source, null)
        }

        clearAdditionalSubmitContent?.()

        if (shouldClearTextOnCommandSubmit) {
          setText('')
        } else {
          const textAfterCommand = text.substring(text.indexOf(' ') + 1).trim()
          setIsAgentTaskSubmitInProgress(true)
          setText(`Creating ${textAfterCommand}...`)
        }
        return
      }

      if (!isLoading) {
        let messageToSubmit = text
        if (!messageToSubmit) {
          if (canSubmitWithEmptyText) {
            messageToSubmit = ''
          } else {
            // get references from state
            const refs = sendableReferences
            const [inferred, newMessage] = manager.tryInferMessage(refs)
            if (!inferred || !newMessage) {
              return
            } else {
              messageToSubmit = newMessage
            }
          }
        }
        if (activeModeName === ModeSupportName.CreateIssue) {
          messageToSubmit = `/create-issue ${messageToSubmit}`
        }
        try {
          if (shouldClearTextOnSubmit) {
            setText('')
          } else {
            setIsAgentTaskSubmitInProgress(true)
            setText(prev => `Creating ${prev}...`)
          }
          const additionalReferences: CopilotChatReference[] = []

          // Build task request reference if this is an agent command
          if (isAgent && selectedRepo) {
            const descriptionWithRefs =
              serializedReferenceURLs.length > 0
                ? `${messageToSubmit}\n\n${serializedReferenceURLs.join('\n')}`
                : messageToSubmit
            const taskRequestReference: TaskRequestReference = {
              type: 'task-request',
              description: descriptionWithRefs,
              repoOwner: selectedRepo.owner,
              repoName: selectedRepo.nameOnly,
              ref: selectedBranch || selectedRepo.defaultBranch,
              customAgent: selectedAgent.subAgentSlug,
              agentId: selectedAgent.topLevelAgentId,
            }
            additionalReferences.push(taskRequestReference)
          }

          await onSubmit?.(messageToSubmit, additionalReferences.length > 0 ? additionalReferences : undefined)

          if (!shouldClearTextOnSubmit) {
            copilotLocalStorage.clearSavedUserMessage(selectedThreadID, source)
          }
        } catch (error) {
          // On error, stop any loading states that disables the submit button
          setIsAgentTaskSubmitInProgress(false)
          // If the error is a validation error, preserve the text so the user can see their input
          // The error handling (like showing validation messages) should be handled by the onSubmit callback
          if (error instanceof ChatInputValidationError) {
            // Don't re-throw validation errors to avoid console errors - the onSubmit callback should handle displaying them
            setText(messageToSubmit)
            return
          }
          // Re-throw unexpected errors as they indicate real problems that need to be logged/handled
          throw error
        }
      }
    },
    [
      selectedThreadID,
      source,
      state,
      text,
      activeModeName,
      command,
      selectedRepo,
      manager,
      navigate,
      availableCommands,
      selectedBranch,
      selectedAgent,
      isLoading,
      setLastUsedRepo,
      shouldClearTextOnCommandSubmit,
      sendableReferences,
      shouldClearTextOnSubmit,
      canSubmitWithEmptyText,
      onSubmit,
      getAdditionalSubmitText,
      clearAdditionalSubmitContent,
    ],
  )

  const handleSubmitToNewThread = useCallback(
    () =>
      manager.sendMessageToNewThread(selectedThreadID, text, sendableReferences, state.context, defaultModel, source),
    [manager, selectedThreadID, text, sendableReferences, state.context, defaultModel, source],
  )

  useEffect(() => {
    const handlePrompt = async () => {
      const prompt = getPromptFromSearchParams(mode, pathname, searchParams)
      const updatedSearchParams = new URLSearchParams(searchParams)
      const queryModel = updatedSearchParams.get('model')
      const modelAvailable = !queryModel || queryModel === model.id

      if (prompt && modelAvailable && !handleAutoSubmitRef.current) {
        updatedSearchParams.delete('prompt')
        updatedSearchParams.delete('model')
        updatedSearchParams.delete('autosubmit')
        sendEvent('dotcom_chat.activate', {target: 'COPILOT_PROMPT_LOADED_FROM_URL', mode: 'immersive'})
        setSearchParams(updatedSearchParams, {replace: true})
        const shouldAutoSubmit = autoSubmit || searchParams.get('autosubmit') === 'true'
        if (shouldAutoSubmit && prompt.trim().length > 0) {
          handleAutoSubmitRef.current = true
          await Promise.resolve()
          await handleSubmit()
        }
      } else if (searchParams.get('autosubmit')) {
        updatedSearchParams.delete('autosubmit')
        setSearchParams(updatedSearchParams, {replace: true})
      }
    }
    void handlePrompt()
  }, [pathname, searchParams, setSearchParams, mode, autoSubmit, model, handleSubmit])

  const handleChange = useCallback(
    // eslint-disable-next-line react-hooks/preserve-manual-memoization
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setTypingHasStarted(true)

      if (!textAreaRef?.current) return

      const newValue = (e.target as HTMLTextAreaElement).value

      // Critical: Update text immediately for responsive typing
      setText(newValue)

      clearPastedFile?.()
      recallIndex.current = 0

      // Scroll can be throttled
      throttledHandleScroll()
    },
    [throttledHandleScroll, clearPastedFile, textAreaRef],
  )

  const handleStop = useCallback(async () => {
    await manager.stopStreaming()

    // Because the request didn't complete, the client only has the local/ephemeral copy of the streamed copilot response,
    // which doesn't have the real message id.
    // Re-fetch the messages from the server so that we have the final state with the correct message ids.
    if (selectedThreadID != null) {
      // Block new messages or retries from being sent while we're reloading the thread
      manager.startThreadReload()
      // Wait 6 seconds for server to complete the saving of the message
      // This matches server-side cancelation logic timing
      await new Promise<string>(timerDone => {
        reloadThreadTimerRef.current = setTimeout(() => timerDone(selectedThreadID), 6 * 1000) as unknown as number
      })
      reloadThreadFetchMessagesCallbackRef.current = await manager.fetchMessages(selectedThreadID, true, true)
    }
  }, [manager, selectedThreadID])

  // Once messages change in state, unblock registered fetchMessages await
  useEffect(() => {
    if (reloadThreadFetchMessagesCallbackRef.current) {
      reloadThreadFetchMessagesCallbackRef.current()
      reloadThreadFetchMessagesCallbackRef.current = () => {}
    }
  }, [state.messages])

  useEffect(() => {
    // Cleanup function to clear the reload timer on unmount or re-render
    return () => {
      // Return cleanup function - when component unmounts, stop the timer so that it doesn't try to reload the old thread
      if (reloadThreadTimerRef.current) {
        clearTimeout(reloadThreadTimerRef.current)
        reloadThreadTimerRef.current = null
      }
      // Also cancel the fetchMessages callbacks
      manager.cancelThreadReload()
      reloadThreadFetchMessagesCallbackRef.current = () => {}
    }
  }, [manager])

  useEffect(() => {
    // Only process chat commands for new conversations unless task within chat thread is enabled
    if (availableCommands.length === 0 && !copilotFeatureFlags.taskWithinChatThread) {
      return
    }

    const newCommand = extractChatCommandType(
      text,
      availableCommands.map(cmd => cmd.key),
    )
    if (command !== newCommand) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setCommand(newCommand)
      onChatCommandChange?.(newCommand)
    }
  }, [text, command, onChatCommandChange, availableCommands])

  return {
    typingHasStarted,
    textAreaRef,
    textareaPreviewRef,
    textAreaPreviewContainerRef,
    text,
    setText,
    isAgentTaskSubmitInProgress,
    command,
    setCommand,
    handleSubmit,
    handleSubmitToNewThread,
    handleScroll: throttledHandleScroll,
    handleChange,
    handleStop,
    handleFocus,
    lastUsedRepo,
    setLastUsedRepo,

    // task
    selectedRepo,
    setSelectedRepo: onSelectRepo,
    selectedBranch,
    setSelectedBranch,
    selectedAgent,
    setSelectedAgent,
    initialRepoNWO,
  }
}

function getPromptFromSearchParams(mode: CopilotChatMode, pathname: string, searchParams: URLSearchParams) {
  if (mode === 'immersive' && /^\/copilot(\/agents|\/spaces\/[a-zA-Z0-9-_]+\/[0-9]+)?\/?$/.test(pathname)) {
    // Load prompt from URL if it exists
    const prompt = searchParams.get('prompt')
    if (prompt) {
      return prompt
    }
  }
  return undefined
}

export function convertChatRepoToAgentDropdownRepository(repo: CopilotChatRepo): AgentDropdownRepository | undefined {
  if (repo) {
    const nwo = `${repo.ownerLogin}/${repo.name}`
    return {
      nameWithOwner: nwo,
      name: nwo,
      nameOnly: repo.name,
      owner: repo.ownerLogin,
      defaultBranch: repo.defaultBranch,
      private: repo.visibility !== 'public',
      isEmpty: false,
      enabled: true,
      id: repo.id,
    }
  }
}
